/**
 * The desktop navigation (dc.html:64-81).
 *
 * `.ft-side` is `display:none` until 1120px and `display:flex` above it, so on a phone this whole
 * subtree is removed from the layout AND from the accessibility tree — which is what stops it and
 * `<BottomNav>` announcing two identical "Main navigation" landmarks at the same time.
 *
 * It takes its items as a prop rather than building them: `AppShell` calls `buildNavItems` once
 * and hands the same array to both navigations, so the two provably cannot drift.
 *
 * ONE DELIBERATE DEPARTURE FROM THE PROTOTYPE'S MARKUP: the prototype put
 * `aria-label="Main navigation"` on an `<aside>` and then nested a second, unlabelled `<nav>`
 * inside it — a complementary landmark named as if it were a navigation one, with a real
 * navigation landmark hidden inside. This is a single `<nav>` carrying the label, and the button
 * list is a plain grid. Same pixels, one correctly-named landmark.
 */

import type { ReactElement } from 'react'

import { BrandPaw, SpriteIcon } from '../model/icons'
import type { NavItemView, ScreenId } from '../model/nav'

export interface SidebarProps {
  readonly items: readonly NavItemView[]
  readonly onNavigate: (screen: ScreenId) => void
  /** Derived from the dog on screen — never a hard-coded name. */
  readonly privacyNote: string
}

export function Sidebar({ items, onNavigate, privacyNote }: SidebarProps): ReactElement {
  return (
    <nav
      className="ft-side"
      aria-label="Main navigation"
      style={{
        position: 'fixed',
        left: '18px',
        top: '18px',
        bottom: '18px',
        width: '272px',
        flexDirection: 'column',
        padding: '26px 18px 20px',
        background: 'var(--color-surface)',
        borderRadius: 'calc(var(--radius-lg) * 1.15)',
        boxShadow: 'var(--shadow-sm)',
        zIndex: 20,
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '2px 8px 26px' }}>
        <span
          aria-hidden="true"
          style={{
            width: '46px',
            height: '46px',
            flex: 'none',
            borderRadius: '50%',
            display: 'grid',
            placeItems: 'center',
            background: 'var(--color-accent)',
            color: 'var(--color-bg)',
          }}
        >
          <BrandPaw />
        </span>
        <span>
          <span style={{ display: 'block', fontFamily: 'var(--font-heading)', fontSize: '19px', lineHeight: 1.1 }}>
            Fairy Tails
          </span>
          <span className="text-muted" style={{ display: 'block', fontSize: '12px', marginTop: '2px' }}>
            My Dog · client portal
          </span>
        </span>
      </div>

      <div style={{ display: 'grid', gap: '6px' }}>
        {items.map((item) => (
          <button
            key={item.id}
            type="button"
            data-nav={item.id}
            /*
              A STRING, never a boolean. React drops an attribute whose value is literal `false`,
              which would silently delete the "you are here" announcement rather than negate it —
              `buildNavItems` returns 'page' | 'false' precisely so this cannot be got wrong here.
            */
            aria-current={item.current}
            onClick={() => onNavigate(item.id)}
            style={{
              position: 'relative',
              display: 'flex',
              alignItems: 'center',
              gap: '12px',
              border: 0,
              cursor: 'pointer',
              padding: '13px 16px',
              borderRadius: '999px',
              font: 'inherit',
              fontSize: '15px',
              fontWeight: 600,
              textAlign: 'left',
              background: item.background,
              color: item.foreground,
            }}
          >
            <SpriteIcon icon={item.icon} />
            {item.label}
            {item.badge ? (
              <span
                aria-hidden="true"
                style={{
                  position: 'absolute',
                  top: '12px',
                  left: '34px',
                  width: '8px',
                  height: '8px',
                  borderRadius: '50%',
                  background: 'var(--color-accent)',
                }}
              />
            ) : null}
          </button>
        ))}
      </div>

      <span style={{ flex: 1 }} />

      <div
        style={{
          padding: '14px 16px',
          borderRadius: '22px',
          background: 'var(--color-neutral-100)',
          fontSize: '12px',
          lineHeight: 1.5,
          color: 'var(--color-neutral-700)',
        }}
      >
        <p style={{ margin: 0 }}>{privacyNote}</p>
        {/*
          A button, not an <a>. There is no router and no /privacy URL — navigation here is reducer
          state — so an anchor would either need a dead href or would reload the app and drop the
          client back at Home. It is styled as a link because that is what it behaves like.
        */}
        <button
          type="button"
          onClick={() => onNavigate('privacy')}
          style={{
            marginTop: '8px',
            padding: 0,
            border: 0,
            background: 'none',
            font: 'inherit',
            fontWeight: 700,
            color: 'var(--color-accent-800)',
            textDecoration: 'underline',
            cursor: 'pointer',
          }}
        >
          How we look after your information
        </button>
      </div>
    </nav>
  )
}
